import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { useAudience } from '../audience';
import { bookingStateColor, bookingStateLabelKey, isOnRequest } from '../bookingState';
import type { TravelBooking } from '../queries';

interface Props {
  /** La reserva, cuando ya existe: es la autoridad del badge y del matiz. */
  booking: TravelBooking | null;
  /** Título de la oferta o de la reserva, ya resuelto por la autoridad de display. */
  title: string;
  /** Quién presta el servicio. Procedencia: solo para el asesor. */
  provider?: string;
}

const STATE_COLOR = {
  confirmed: 'success',
  cancelled: 'error',
  failed: 'error',
} as const;

/**
 * Quién es esta reserva y en qué momento está, en una línea.
 *
 * El badge es el estado: neutro mientras se revisa, y una vez que existe la
 * reserva el del contrato — que ya sabe que un `confirmed` on-request NO se
 * lee "confirmado".
 *
 * El `providerStatus` va al lado como MATIZ del estado unificado, nunca en su
 * lugar. Se pinta el de la respuesta que está en pantalla y nada más: el modal
 * reemplaza el booking entero con cada respuesta (`setBooking(updated)`), así
 * que un matiz ausente desaparece en vez de quedar pegado del intento
 * anterior, que es lo que exige el contrato.
 */
export function BookingMomentHeader({ booking, title, provider }: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {booking ? (
          <Tooltip
            title={
              booking.state === 'held' ? t('travelServices.booking.holdTooltip') : ''
            }
          >
            <Chip
              size="small"
              sx={{ cursor: booking.state === 'held' ? 'help' : 'default' }}
              color={bookingStateColor(
                booking.state,
                booking.providerStatus,
                STATE_COLOR,
                'info',
              )}
              label={t(bookingStateLabelKey(booking.state, booking.providerStatus))}
            />
          </Tooltip>
        ) : (
          <Chip size="small" label={t('travelServices.booking.moments.review')} />
        )}
        {/* El matiz del proveedor. El on-request no se repite acá: el badge ya
            lo dice con todas las letras en vez de cantar "confirmado". */}
        {booking?.providerStatus && !isOnRequest(booking.providerStatus) && (
          <Chip size="small" variant="outlined" label={booking.providerStatus} />
        )}
        <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
          {title || '—'}
        </Typography>
      </Stack>
      <Box display="flex" gap={1} flexWrap="wrap">
        {/* Procedencia, no argumento de venta: el proveedor y el hub que lo
            trae son información de operación del asesor. */}
        {isAgent && provider && (
          <Typography variant="caption" color="text.secondary">
            {t('travelServices.booking.viaHub', { provider })}
          </Typography>
        )}
        {booking?.providerReference && (
          <Typography variant="caption" color="text.secondary">
            {t('travelServices.booking.providerReference', {
              reference: booking.providerReference,
            })}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}
